import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Divider, Chip, CircularProgress, Paper, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';


function PorticoPopup({ portico, concesionaria, position, onClose, view }) {
  const [tarifas, setTarifas] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Safe access to view dimensions
  const viewportWidth = view ? (view.width || window.innerWidth) : window.innerWidth;

  // Estimate popup dimensions (Portico popup is larger)
  const pWidth = 350;
  const pHeight = 400;

  const arrowSize = 20;
  const offset = 12;

  // Initial values to avoid errors if position is null
  const x = position ? position.x : 0;
  const y = position ? position.y : 0;

  // Determine horizontal placement
  let placeRight = true;
  if (x + pWidth + offset > viewportWidth) {
    placeRight = false;
  }

  // Determine vertical placement
  let placeTop = true;
  if (y - pHeight - offset < 0) {
    placeTop = false;
  }

  // Calculate coordinates and transforms based on quadrant
  let left = x;
  let top = y;
  let transform = '';
  let origin = '';

  // Styles for the arrow (pseudo-element)
  let arrowStyle = {
    content: '""',
    position: 'absolute',
    width: 0,
    height: 0,
    borderStyle: 'solid',
    borderColor: 'transparent',
  };

  if (placeTop) {
    top -= offset;
    if (placeRight) {
      // Quadrant: Top-Right
      left += offset - 8;
      transform = 'translate(0, -100%)';
      origin = 'bottom left';
      arrowStyle = {
        ...arrowStyle,
        bottom: `-${offset}px`,
        left: '0px',
        borderWidth: `${arrowSize}px ${arrowSize}px 0 0`,
        borderTopColor: 'white'
      };
    } else {
      // Quadrant: Top-Left
      left -= offset - 8;
      transform = 'translate(-100%, -100%)';
      origin = 'bottom right';
      arrowStyle = {
        ...arrowStyle,
        bottom: `-${offset}px`,
        right: '0px',
        borderWidth: `${arrowSize}px 0 0 ${arrowSize}px`,
        borderTopColor: 'white'
      };
    }
  } else {
    top += offset;
    if (placeRight) {
      // Quadrant: Bottom-Right
      left += offset - 8;
      transform = 'translate(0, 0)';
      origin = 'top left';
      arrowStyle = {
        ...arrowStyle,
        top: `-${offset}px`,
        left: '0px',
        borderWidth: `0 ${arrowSize}px ${arrowSize}px 0`,
        borderBottomColor: 'white'
      };
    } else {
      // Quadrant: Bottom-Left
      left -= offset - 8;
      transform = 'translate(-100%, 0)';
      origin = 'top right';
      arrowStyle = {
        ...arrowStyle,
        top: `-${offset}px`,
        right: '0px',
        borderWidth: `0 0 ${arrowSize}px ${arrowSize}px`,
        borderBottomColor: 'white'
      };
    }
  }

  const fetchTarifas = async () => {
    if (!portico || !portico.id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${process.env.REACT_APP_VALHALLA_URL_BACKEND}/api/vehicles/porticos/${portico.id}/tarifas`);
      if (response.data.success) {
        setTarifas(response.data.data);
      } else {
        setError(response.data.error || 'Error al obtener tarifas');
      }
    } catch (err) {
      setError('Error de conexión al servidor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (portico && portico.id) {
      fetchTarifas();
    }
  }, [portico?.id]);


  // Función para formatear valores de las tarifas
  const formatTarifa = (tarifa) => {
    if (!tarifa) return 'N/A';
    return `$${tarifa}`;
  };

  const getTipoChip = () => {
    const color = portico.tipo === 'FREEFLOW' ? 'primary' :
      portico.tipo === 'TAG' ? 'success' : 'default';
    return (
      <Chip
        label={portico.tipo}
        color={color}
        size="small"
        sx={{ fontWeight: 'bold' }}
      />
    );
  };

  return (
    <Paper
      elevation={4}
      sx={{
        position: 'absolute',
        left: `${left}px`,
        top: `${top}px`,
        transform: transform,
        transformOrigin: origin,
        zIndex: 2000,
        p: 0, // Remove padding from Paper to let internal Box handle it
        width: `${pWidth}px`,
        // maxHeight removed from here to allow arrow visibility
        // overflowY removed/visible needed for arrow
        overflow: 'visible',
        backgroundColor: 'white',
        borderRadius: 2,
        animation: 'fadeIn 0.2s ease-out',
        '@keyframes fadeIn': {
          from: { opacity: 0, transform: `${transform} scale(0.9)` },
          to: { opacity: 1, transform: `${transform} scale(1)` }
        },
        '&:before': arrowStyle
      }}
    >
      <Box
        sx={{
          position: 'relative',
          maxHeight: 400,
          overflowY: 'auto',
          p: 2 // Padding moved here
        }}
      >
        <IconButton
          size="small"
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            backgroundColor: 'white',
            '&:hover': { backgroundColor: '#f5f5f5' },
            zIndex: 1,
            border: '1px solid #ccc',
          }}
          onClick={onClose}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Typography variant="h6" sx={{ mb: 1, pr: 3 }}>
          {portico.nombre}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" component="span" sx={{ mr: 1 }}>
            Tipo:
          </Typography>
          {getTipoChip()}
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">
            Concesionaria:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {concesionaria.concesionaria}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">
            Referencia:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {portico.referencia || 'No disponible'}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">
            Código de referencia:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {portico.cod_ref || 'No disponible'}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Tarifas
        </Typography>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {error && (
          <Box sx={{ p: 1 }}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        )}

        {!loading && tarifas && tarifas.portico && tarifas.portico.tarifas && (
          <Box>
            {tarifas.portico.tarifas.map((tarifaGroup, groupIndex) => (
              <Box key={groupIndex} sx={{ mb: 2 }}>
                {tarifaGroup.map((tarifa, tarifaIndex) => (
                  <Box key={tarifaIndex} sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">
                      {tarifa.tipo} ({tarifa.fecha_ini.substring(0, 10)} a {tarifa.fecha_fin.substring(0, 10)})
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                      {tarifa.categoria.map((cat) => (
                        <Chip
                          key={cat.tipo_categoria}
                          label={`Cat ${cat.tipo_categoria}: ${formatTarifa(cat.tarifa)}`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        )}

        {!loading && (!tarifas || !tarifas.portico || !tarifas.portico.tarifas) && !error && (
          <Typography variant="body2" color="text.secondary">
            No hay información de tarifas disponible
          </Typography>
        )}

        <Button
          variant="outlined"
          color="primary"
          fullWidth
          sx={{ mt: 2 }}
          onClick={fetchTarifas}
        >
          Actualizar tarifas
        </Button>
      </Box>
    </Paper>
  );
}

export default PorticoPopup; 